<template>
	<div class="home-page" @click="closeUserInfo">
		<div class="app-container" :class="{ fullscreen: configStore.fullScreen }">
			<div class="navi-bar">
				<div class="navi-bar-box">
					<div class="top">
						<div class="user-head-image">
							<head-image :name="userStore.userInfo.name" :size="38"
								:url="userStore.userInfo.headUrl" @click.native="showSettingDialog = true">
							</head-image>
						</div>
						<div class="menu">
							<!-- 数据统计 -->
							<router-link class="link" v-bind:to="'/home/statistics'">
								<div class="menu-item">
								<span class="icon iconfont icon-statistics"></span>
								</div>
							</router-link>
							<!-- 聊天 -->
							<router-link class="link" v-bind:to="'/home/chat'">
								<div class="menu-item">
									<span class="icon iconfont icon-chat"></span>
									<div v-show="unreadCount > 0" class="unread-text">{{ unreadCount }}</div>
								</div>
							</router-link>
							<!-- 好友 -->
							<router-link class="link" v-bind:to="'/home/friend'">
								<div class="menu-item">
									<span class="icon iconfont icon-friend"></span>
								</div>
							</router-link>
							<!-- 群组 -->
							<router-link class="link" v-bind:to="'/home/group'">
								<div class="menu-item">
									<span class="icon iconfont icon-group" style="font-size: 28px"></span>
								</div>
							</router-link>
						</div>
					</div>
					<div class="botoom">
						<div class="bottom-item" @click="onSwtichFullScreen">
							<i class="el-icon-full-screen"></i>
						</div>
						<div class="bottom-item" @click="showSetting">
							<span class="icon iconfont icon-setting" style="font-size: 20px"></span>
						</div>
						<div class="bottom-item" @click="onExit()" title="退出">
							<span class="icon iconfont icon-exit"></span>
						</div>
					</div>
				</div>
			</div>
			<div class="content-box">
				<router-view></router-view>
			</div>
			<setting :visible="showSettingDialog" @close="closeSetting()"></setting>
			<user-info ref="userInfo"></user-info>
			<full-image ref="fullImage"></full-image>
		</div>
	</div>
</template>

<script>
import HeadImage from '../components/common/HeadImage.vue';
import Setting from '../components/setting/Setting.vue';
import UserInfo from '../components/common/UserInfo.vue';
import FullImage from '../components/common/FullImage.vue';
import { getAllConversationsV2 } from '../api/conversation.js'
import { autoRemark } from '../utils/conversation.js'
import { getMessages, getMessagesBatch, getMessagesPage } from '../api/message.js'
import { quit } from '../api/auth.js'

// 增量同步满页判定阈值：与后端 GetMessages/GetMessagesBatch 的单会话 limit 保持一致（见 messaging_repo.go）
const SYNC_PAGE_LIMIT = 500;
// 增量缺口翻页每页条数（GetMessagesPage 的 limit，见 conversation.go GetMessagesPage）
const GAP_PAGE_SIZE = 100;
// 单会话翻页轮数上限：100 条/页 × 20 = 2000 条，防止异常场景死循环
const MAX_SYNC_PAGE_ROUNDS = 20;
// 周期增量同步兜底间隔：WS 推送静默丢失（代理丢包/队列丢弃/后台节流）时的补偿窗口
const PERIODIC_SYNC_INTERVAL = 60000;

export default {
	components: {
		HeadImage,
		Setting,
		UserInfo,
		FullImage
	},
	data() {
		return {
			showSettingDialog: false,
			lastPlayAudioTime: new Date().getTime() - 1000,
			reconnecting: false,
			reconnectRetryCount: 0,
			reconnectTimer: null,             // 重连退避/慢重试定时器（销毁时需清理）
			privateMessagesBuffer: [],
			syncFailCount: 0,
			syncInFlight: 0,            // 会话同步并发守卫计数：>0 表示有同步链在途（失败重试可并行，计数配平）
			initialSyncStarted: false,  // 首次会话同步是否已发起（兜底/onConnect 任一），迟到连接据此走增量语义
			wsConnectTimer: null,
			// 性能优化：实时消息 50ms 合并窗口
			realtimeMsgBuffer: [],
			realtimeMsgTimer: null,
			REALTIME_BATCH_WINDOW: 50,
			periodicSyncTimer: null,      // 周期增量同步定时器（WS 推送丢失兜底）
			periodicSyncInFlight: false,  // 防止周期同步与重连同步并发
		}
	},
	methods: {
		init() {
			this.$eventBus.$on('openUserInfo', (user, pos) => {
				// 打开用户卡片
				this.$refs.userInfo.open(user, pos);
			});
			this.$eventBus.$on('openFullImage', url => {
				// 图片全屏
				this.$refs.fullImage.open(url);
			});
			// app初始化状态-false
			this.configStore.setAppInit(false) 
			this.loadStore().then(() => {
				// ws初始化（连接时实时读取 token，无需外部传入）
				this.$wsApi.connect(process.env.VUE_APP_WS_URL);
				// WS 连接超时兜底：10s 未收到 onConnect 则走 HTTP 加载数据
				this.wsConnectTimer = setTimeout(() => {
					if (!this.wsConnectTimer) return;
					this.wsConnectTimer = null;
					console.warn('[Home] WS 连接超时，走 HTTP 兜底加载');
					// 兜底已发起会话同步：WS 迟到连上时 onConnect 须按增量语义同步
					this.initialSyncStarted = true;
					this.syncConversation(false);
					this.configStore.setAppInit(true);
				}, 10000);
				// 链接成功回调
				this.$wsApi.onConnect(() => {
					clearTimeout(this.wsConnectTimer);
					if (this.reconnecting) {
						this.onReconnectWs();
					} else {
						// 加载离线消息
						// this.pullOfflineMessage();
						// 改为同步服务器会话
						// 迟到连接（10s 兜底/页面恢复直连已先发起过同步，cacheChats 已被消费）：
						// 按增量（reConnect=true）语义同步，避免非重连分支以失效基准全量重载
						const incremental = this.initialSyncStarted === true;
						this.initialSyncStarted = true;
						this.syncConversation(incremental)
						this.configStore.setAppInit(true);
					}
					// WS 推送丢失兜底：启动周期增量同步（幂等，首次/重连后自动续期）
					this.startPeriodicSync();
				});
				// 接收到信息回调
				this.$wsApi.onMessage((cmd, msgInfo) => {
					// 坏数据保护：单条非法 JSON 直接跳过，不中断后续消息分发
					let msg;
					try {
						msg = JSON.parse(msgInfo);
					} catch (e) {
						console.warn(`[WS] 忽略无法解析的消息 cmd=${cmd}`, msgInfo);
						return;
					}
					if (cmd == 2) {
						// 关闭ws
						this.$wsApi.close(3000)
						// 异地登录，强制下线
						this.$alert("您已在其他地方登录，将被强制下线", "强制下线通知", {
							confirmButtonText: '确定',
							callback: action => {
								location.href = "/";
							}
						});
					} else if (cmd == 3 || cmd == 4) {
						// 后端所有新消息（私聊+群聊）都通过 cmd=3 发送
						// cmd=4 保留兼容旧版后端群聊消息
						if (!this.configStore.appInit || this.chatStore.loading) {
							this.privateMessagesBuffer.push(msg);
						} else {
							// 性能优化：首条消息立即处理零延迟，50ms 内后续消息合并批量处理
							if (!this.realtimeMsgTimer) {
								this.handleNewMessage(msg);
								this.realtimeMsgTimer = setTimeout(() => {
									this.realtimeMsgTimer = null;
									if (this.realtimeMsgBuffer.length > 0) {
										this.flushRealtimeBuffer();
									}
								}, this.REALTIME_BATCH_WINDOW);
							} else {
								this.realtimeMsgBuffer.push(msg);
							}
						}
					} else if (cmd == 5) {
						// 处理系统消息
						this.handleSystemMessage(msg);
					} else if (cmd == 11) {
						// 消息已读通知
						this.chatStore.newReadedMessage(msg);
					} else if (cmd == 12) {
						// 消息发送失败通知
						this.chatStore.markMessageFailed(msg);
					} else {
						console.warn(`[WS] Unhandled cmd: ${cmd}`, msgInfo);
					}
				});
				// 关闭回调：任何非主动断开都重新进入重连流程（含 token 过期场景，
				// loadUser 前置校验会触发 HTTP 刷新，重连自动使用新 token）
				this.$wsApi.onClose((e) => {
					if (e.code != 3000) {
						this.reconnecting = false;
						this.reconnectWs();
						this.configStore.setAppInit(false)
						// 通知Chat组件连接断开
						this.$eventBus.$emit('ws-disconnected');
					}
				});
			}).catch((e) => {
				console.error('[Home] init 失败:', e);
				this.$message.error('初始化失败，请刷新页面重试');
			})
		},
		reconnectWs() {
			const MAX_RETRY = 5;
			if (this.reconnectRetryCount >= MAX_RETRY) {
				// 达到上限后降频慢重试：30s 后重置计数自动再试，避免 WS 永久停摆
				this.reconnecting = false;
				this.reconnectRetryCount = 0;
				this.$message.error("连接失败次数过多，30秒后将自动重试，也可手动刷新页面");
				this.reconnectTimer = setTimeout(() => this.reconnectWs(), 1000 * 30);
				return;
			}
			this.reconnectRetryCount++;
			// 记录标志
			this.reconnecting = true;
			// 重新加载一次个人信息，目的是为了保证网络已经正常且token有效
			// （401 时触发 HTTP 静默刷新，sessionStorage 更新后重连自动用新 token）
			this.userStore.loadUser().then(() => {
				this.$message.error("连接断开，正在尝试重新连接...");
				// 递增退避：WS 连续失败时延迟 1s→2s→…→30s 封顶，避免快速失败循环
				const delay = Math.min(1000 * this.reconnectRetryCount, 30000);
				this.reconnectTimer = setTimeout(() => {
					// 连接内部实时读取最新 token
					this.$wsApi.connect(process.env.VUE_APP_WS_URL);
				}, delay);
			}).catch(() => {
				// 5s后重试
				this.reconnecting = false;
				this.reconnectTimer = setTimeout(() => this.reconnectWs(), 1000 * 5)
			})
		},
		onReconnectWs() {
			// 重连成功
			this.reconnecting = false;
			this.reconnectRetryCount = 0;
			this.initialSyncStarted = true; // 会话同步已发起过，后续连接事件一律走增量语义
			// 通知Chat组件连接已恢复
			this.$eventBus.$emit('ws-connected');
			// 重新加载群和好友
			const promises = [];
			// 登录全量加载好友（pageSize=0 全量，gzip 后 600KB 级；失败降级不阻塞重连流程）
			promises.push(this.friendStore.loadFriend({ reset: true, pageSize: 0 }).catch(() => {}));
			promises.push(this.groupStore.loadGroup());
			Promise.all(promises).then(() => {
				// 加载离线消息
				// this.pullOfflineMessage();
				// 改为同步服务器会话
				this.syncConversation(true)
				this.configStore.setAppInit(true)
				this.$message.success("重新连接成功");
			}).catch(() => {
				this.$message.error("初始化失败，5秒后自动重试...");
				setTimeout(() => this.onReconnectWs(), 5000);
			})
		},
		loadStore() {
			console.log('[Home] loadStore 开始...');
			return this.userStore.loadUser().then(() => {
				console.log('[Home] loadUser 成功, 开始加载 stores...');
				const promises = [];
				// 防御：非关键配置接口失败降级不阻塞初始化（单个接口 404/超时——
				// 如后端版本落后于前端新增调用——不应炸掉整条加载链导致会话列表不加载）
				promises.push(this.chatStore.loadChatlabels().catch(e => {
					console.warn('[Home] 聊天标签加载失败（不阻塞初始化）:', e?.message || e);
				}));  // 聊天标签分组
				promises.push(this.chatStore.loadQuickReplies().catch(e => {
					console.warn('[Home] 快捷回复加载失败（不阻塞初始化）:', e?.message || e);
				}));  // 快捷回复
				promises.push(this.chatStore.loadAutoReplyScripts().catch(e => {
					console.warn('[Home] 自动回复话术加载失败（不阻塞初始化）:', e?.message || e);
				}));  // 自动回复话术
				promises.push(this.chatStore.loadFollowUpConfig().catch(e => {
					console.warn('[Home] 超时跟进配置加载失败（不阻塞初始化）:', e?.message || e);
				}));  // 超时跟进配置
				promises.push(this.chatStore.loadChat());         // 会话
				// 好友列表：登录全量加载（pageSize=0，gzip 后传输可接受），
				// 失败降级不阻塞页面初始化（好友页/选人面板可补拉）
				promises.push(this.friendStore.loadFriend({ reset: true, pageSize: 0 }).catch(e => {
					console.warn('[Home] 好友列表全量加载失败（不阻塞初始化）:', e);
				}));     // 好友列表
				promises.push(this.groupStore.loadGroup().catch(e => {
					console.warn('[Home] 群组列表加载失败（不阻塞初始化）:', e?.message || e);
				}));       // 群组列表
				this.chatStore.initSaveGuard();                   // 注册关闭页面时保存数据
				return Promise.all(promises).then(() => {
					console.log('[Home] loadStore 全部完成, 好友:', this.friendStore.friends.length, '群组:', this.groupStore.groups.length);
				});
			}).catch(e => {
				console.error('[Home] loadStore 失败:', e);
				throw e;
			})
		},
		unloadStore() {
			this.friendStore.clear();
			this.groupStore.clear();
			this.chatStore.clear();
			this.userStore.clear();
		},
		// 同步服务器会话
		syncConversation(reConnect, isRetry = false, silent = false) {
			// 并发守卫：同一时刻只允许一条同步链在途（10s 连线超时兜底与迟到 onConnect、
			// visibilitychange 直连与重连定时器竞争等均可能双触发）。双链并发时后完成者
			// 会以过期基准 refreshChats，造成会话对象被服务端快照替换（打开中会话不更新
			// 消息、发送回显丢失）。非重试调用在途时跳过：在途链的完成/重试已覆盖同样的
			// 同步目标。失败重试（isRetry）放行：其为恢复关键路径，跳过会把恢复推迟到
			// 下一轮周期同步；syncChats 的 base 兜底保证即使短暂并行也不会对象替换。
			// 计数配平（而非布尔）防止并行链先后结束时守卫被提前释放
			if (this.syncInFlight > 0 && !isRetry) {
				console.warn('[Home] syncConversation 跳过：已有同步链在途', { reConnect, isRetry, silent });
				return Promise.resolve();
			}
			if (!isRetry) this.syncFailCount = 0;
			this.syncInFlight++;
			// loading 保留"数据面翻转中"语义（cacheChats 影子数组的查找/写入目标、
			// WS 缓冲、索引与落盘守卫）；silent 周期同步通过第二参数标记，
			// 仅让 Chat 页占位与高度缓存重建跳过本次切换，消除每 60s 列表闪烁
			this.chatStore.setLoading(true, silent);
			// 必须 return 整个 Promise 链：startPeriodicSync 等调用方依赖返回值做 .finally() 复位标志，
			// 否则 undefined.finally 抛 TypeError 且 periodicSyncInFlight 永远为 true，周期同步永久停摆
			return getAllConversationsV2().then((data) => {
				// 添加空值检查
				if (!data || !data.list) {
					console.warn("服务器返回数据格式异常");
					return Promise.resolve();
				}
				// 同步会话
				if (data.list.length > 0) {
					// 同步会话并获取需要拉取消息的会话数组
					const chatNeedUpdate =this.chatStore.syncChats(reConnect,data.list);
				console.log('[Home] syncChats result', { serverConvs: data.list.length, needUpdate: chatNeedUpdate.length, convIDs: chatNeedUpdate.map(c => c.conversationID) });
					// 如果有需要更新消息的会话，则拉取消息
					if (chatNeedUpdate.length > 0) {
						// 等待消息同步完成后再继续
						// return this.syncMessagesFromDB(chatNeedUpdate);
						// 并发控制版本
						return this.syncMessagesFromDBConcurrency(chatNeedUpdate)
					}
				}
				// 如果没有需要更新的会话，直接返回 resolved Promise
				return Promise.resolve();
			}).then(() => {
				// 性能优化：缓冲区消息批量处理
				const buffered = this.privateMessagesBuffer.splice(0);
				if (buffered.length > 0) {
					this._processBufferedMessages(buffered);
				}
				// 关闭加载标记（silent 同步同参传递，避免误触发 UI 占位切换）
				this.chatStore.setLoading(false, silent);
				// 刷新会话（内部同步排序 + _validateGroupSummary）
				this.chatStore.refreshChats();
				// 成功后重置重试计数
				this.syncFailCount = 0;
			}).catch((e) => {
				if (!silent) {
					this.$message.error("同步服务器会话错误");
				}
				console.error('[Home] 同步服务器会话错误:', e);
				// 重试机制：首次失败自动重试，超过上限提示用户
				this.syncFailCount++;
				const maxSyncRetries = 3;
				if (this.syncFailCount <= maxSyncRetries) {
					const retryDelay = this.syncFailCount * 2000;
					if (!silent) {
						this.$message.warning(`正在重试同步... (${this.syncFailCount}/${maxSyncRetries})`);
					}
					setTimeout(() => this.syncConversation(reConnect, true, silent), retryDelay);
				} else {
					// 最后一次失败：flush 缓冲区 + 清理状态，避免消息永久丢失
					const buffered = this.privateMessagesBuffer.splice(0);
					if (buffered.length > 0) {
						this._processBufferedMessages(buffered);
					}
					this.chatStore.setLoading(false, silent);
					this.chatStore.refreshChats();
					if (!silent) {
						this.$message.error('同步失败次数过多，请检查网络后刷新页面重试');
					}
				}
			}).finally(() => {
				// 释放并发守卫（成功/安排重试/彻底失败路径都会到此），计数配平：
				// 与并行重试链共存时，最后一个结束的链才真正释放
				this.syncInFlight = Math.max(0, this.syncInFlight - 1);
			})
		},
		// WS 推送丢失兜底：定期增量同步（幂等：增量 + 去重），静默失败不打扰用户
		startPeriodicSync() {
			if (this.periodicSyncTimer) return;
			this.periodicSyncTimer = setInterval(() => {
				// 应用未就绪/正在重连/上一轮未结束时跳过，避免并发与误报
				if (this.periodicSyncInFlight || !this.configStore.appInit || this.reconnecting) return;
				this.periodicSyncInFlight = true;
				this.syncConversation(true, false, true).finally(() => {
					this.periodicSyncInFlight = false;
				});
			}, PERIODIC_SYNC_INTERVAL);
		},
		stopPeriodicSync() {
			if (this.periodicSyncTimer) {
				clearInterval(this.periodicSyncTimer);
				this.periodicSyncTimer = null;
			}
		},
		// 为需要更新的会话同步消息
		syncMessagesFromDB(chatNeedUpdate) {
			const syncPromises  = chatNeedUpdate.map(chat => {
				return getMessages(chat).then((data) => {
					if (data.list && data.list.length > 0) {
						this.chatStore.addMessagesToConversation(chat.conversationID, data.list);
					}
					return data;
				}).catch(error => {
					console.error(`同步会话 ${chat.conversationID} 消息失败:`, error);
					return null;
				});
			});
			return Promise.all(syncPromises);
		},
		// 为需要更新的会话同步消息 - 批量接口版本（分批发送，避免单次请求过大）
		async syncMessagesFromDBConcurrency(chatNeedUpdate) {
			if (chatNeedUpdate.length === 0) return;

			const BATCH_SIZE = 100; // 需与后端 maxBatchSessions 保持一致，见 conversation.go
			const batches = [];
			for (let i = 0; i < chatNeedUpdate.length; i += BATCH_SIZE) {
				batches.push(chatNeedUpdate.slice(i, i + BATCH_SIZE));
			}

			let totalMsgs = 0;
			let hasSuccess = false;
			let circuitBroken = false;
			for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
				const batch = batches[batchIdx];
				if (circuitBroken) {
					totalMsgs += await this._fallbackSyncBatch(batch);
					hasSuccess = true;
					continue;
				}
				try {
					const data = await getMessagesBatch(batch);
					if (data && data.results) {
						let batchMsgs = 0;
						// 返回满页（= 后端单会话 limit）说明该会话可能还有更早的增量消息，
						// 收集后用 older 双向游标翻页补齐，防止断线积累 >500 条时中间段永久缺失
						const pageGaps = [];
						for (const [convID, msgs] of Object.entries(data.results)) {
							if (msgs && msgs.length > 0) {
								this.chatStore.addMessagesToConversation(convID, msgs);
								batchMsgs += msgs.length;
								if (msgs.length >= SYNC_PAGE_LIMIT) {
									pageGaps.push({ conversationID: convID, oldest: msgs[0] });
								}
							}
						}
						if (pageGaps.length > 0) {
							batchMsgs += await this._fillPageGaps(pageGaps);
						}
						// 本轮增量查询成功：仅对服务器确实返回结果的会话标记已验证游标（含缺口
						// 翻页补齐后），稳态下 syncChats 不再对同秒会话重复发起无收益增量查询。
						// 注意不能遍历 batch 全量：unauthorized 会话不在此批次结果中（消息未
						// 拉取），但 syncChats 已提前提升其 latestMsgSendTime，若此时标记游标
						// 会形成“时间已同步+游标匹配”的假收敛，权限恢复后增量缺口永久漏补
						for (const item of batch) {
							if (data.results[item.conversationID] !== undefined) {
								this.chatStore.markSyncVerified(item.conversationID);
							}
						}
						totalMsgs += batchMsgs;
						console.log(`[Home] syncMessagesBatch ${batchIdx + 1}/${batches.length}`, { convCount: Object.keys(data.results).length, batchMsgs, gapConvs: pageGaps.length });
					}
					hasSuccess = true;
				} catch (error) {
					console.error(`批量同步消息失败(批大小=${batch.length})，跳过后续批次，回退逐个拉取:`, error);
					circuitBroken = true;
					totalMsgs += await this._fallbackSyncBatch(batch);
					hasSuccess = true;
				}
			}
			if (!hasSuccess && batches.length > 0) {
				throw new Error('All batches and fallbacks failed');
			}
			console.log('[Home] syncMessagesBatch loaded', { batchCount: batches.length, totalMsgs });
		},
		// 满页会话增量缺口补齐：以已拉取的最旧一条为游标，向 older 方向翻页直到服务器确认无更多
		// 修复：断线期间单会话积累 > 后端 limit(500) 条时，单次增量拉取只返回最新 500 条，
		// 中间段消息无任何触发源可补回（hasNewer 已为 false、同秒缺口条件不满足），导致永久缺失
		async _fillPageGaps(gaps) {
			let count = 0;
			await Promise.all(gaps.map(async ({ conversationID, oldest }) => {
				let cursor = oldest.sendTime || 0;
				let cursorID = oldest.id || 0;
				for (let round = 0; round < MAX_SYNC_PAGE_ROUNDS; round++) {
					try {
						const data = await getMessagesPage({
							conversationID,
							cursor,
							cursorID,
							direction: 'older',
							limit: GAP_PAGE_SIZE,
						});
						if (!data || !data.list || data.list.length === 0) return;
						this.chatStore.addMessagesToConversation(conversationID, data.list);
						count += data.list.length;
						// 推进游标到本轮最旧一条，继续探查更早的缺口段
						cursor = data.list[0].sendTime || 0;
						cursorID = data.list[0].id || 0;
						// 服务器 hasMore=false（条数不足一页）时确认无更多历史
						if (!data.hasMore || data.list.length < GAP_PAGE_SIZE) return;
					} catch (e) {
						console.error(`补齐增量缺口失败 conversationID=${conversationID}:`, e);
						return;
					}
				}
				console.warn(`[Home] 增量缺口翻页超过上限(${MAX_SYNC_PAGE_ROUNDS}) conversationID=${conversationID}`);
			}));
			return count;
		},
		async _fallbackSyncBatch(batch) {
			let count = 0;
			for (const chat of batch) {
				try {
					const d = await getMessages(chat);
					if (d && d.list && d.list.length > 0) {
						this.chatStore.addMessagesToConversation(chat.conversationID, d.list);
						count += d.list.length;
						// 满页时同样补齐更早的增量缺口（与批量接口语义一致）
						if (d.list.length >= SYNC_PAGE_LIMIT) {
							count += await this._fillPageGaps([{ conversationID: chat.conversationID, oldest: d.list[0] }]);
						}
					}
					// 单会话查询成功即标记已验证游标（无新消息也标记，驱动稳态收敛）
					this.chatStore.markSyncVerified(chat.conversationID);
				} catch (e) {
					console.error(`回退单会话同步失败 conversationID=${chat.conversationID}:`, e);
				}
			}
			return count;
		},
		// 性能优化：提取 friend 信息构造为复用方法
		buildFriendInfo(msg) {
			if (msg.isWX) {
				return { userID: msg.receiver, showName: msg.receiverName, faceURL: msg.receiverFaceURL };
			}
			return { userID: msg.sender, showName: msg.senderName, faceURL: msg.senderFaceURL };
		},
		// 处理新消息（私聊+群聊统一通过 cmd=3 接收）
		handleNewMessage(msg) {
			// 系统消息（跟进提醒等 sender='sys'）直接创建系统会话
			if (msg.sender === 'sys') {
				this.chatStore.openChat({ conversationID: msg.conversationID, userID: 'sys', showName: '系统通知', convType: 'system' });
				this.chatStore.insertMessage(msg, { conversationID: msg.conversationID });
				return;
			}
			// 删除/拉黑通知（101/102）不再自动归组，仅作为普通消息插入，分组由客服手动维护
			this.insertPrivateMessage(this.buildFriendInfo(msg), msg);
		},
		insertPrivateMessage(friend, msg) {
			let chatInfo = {
				conversationID: msg.conversationID,
				convType: msg.isRoom === 1 ? 'group' : 'single',
				userID: friend.userID,
				showName: friend.showName,
				faceURL: friend.faceURL,
				remark: autoRemark(friend.showName, friend.userID),
			};
			// 打开会话
			this.chatStore.openChat(chatInfo);
			// 插入消息
			this.chatStore.insertMessage(msg, chatInfo);
			// 播放提示音
			const chat = this.chatStore.findChat({ conversationID: msg.conversationID });
			if (chat && !chat.isDnd && !this.chatStore.loading && !msg.isWX && this.$msgType.isNormal(msg.msgType) &&
				msg.status != this.$enums.MESSAGE_STATUS.READED) {
				this.playAudioTip();
			}
		},
		// 性能优化：按 conversationID 分组批量处理消息（共用方法）
		_processBufferedMessages(msgs) {
			if (!msgs || msgs.length === 0) return;
			const byConv = {};
			msgs.forEach(msg => {
				if (msg.sender === 'sys') {
					this.chatStore.openChat({ conversationID: msg.conversationID, userID: 'sys', showName: '系统通知', convType: 'system' });
					this.chatStore.insertMessage(msg, { conversationID: msg.conversationID });
					return;
				}
				if (!byConv[msg.conversationID]) byConv[msg.conversationID] = { msgs: [] };
				byConv[msg.conversationID].msgs.push(msg);
			});
			Object.entries(byConv).forEach(([convID, entry]) => {
				const convMsgs = entry.msgs;
				const lastMsg = convMsgs[convMsgs.length - 1];
				const friend = this.buildFriendInfo(lastMsg);
				this.chatStore.openChat({
					conversationID: convID,
					convType: lastMsg.isRoom === 1 ? 'group' : 'single',
					userID: friend.userID,
					showName: friend.showName,
					faceURL: friend.faceURL,
					remark: autoRemark(friend.showName, friend.userID),
				});
				this.chatStore.addMessagesToConversation(convID, convMsgs);
				const lastNonWx = convMsgs.filter(m => !m.isWX).pop();
				if (lastNonWx) {
					const convChat = this.chatStore.findChat({ conversationID: convID });
					if (convChat && !convChat.isDnd && !this.chatStore.loading && this.$msgType.isNormal(lastNonWx.msgType)) {
						this.playAudioTip();
					}
				}
			});
		},
		// 性能优化：刷新实时消息缓冲区
		flushRealtimeBuffer() {
			const msgs = this.realtimeMsgBuffer.splice(0);
			this.realtimeMsgTimer = null;
			if (msgs.length === 0) return;
			this._processBufferedMessages(msgs);
		},
		// 页面关闭/刷新前 flush 实时缓冲与加载期缓冲到 chatStore，并立即写 IndexedDB
		// 注意：F5 整页刷新时 Vue unmounted 不执行，必须挂到 beforeunload/pagehide 兜底
		_flushBuffersOnUnload() {
			if (this.realtimeMsgTimer) {
				clearTimeout(this.realtimeMsgTimer);
				this.realtimeMsgTimer = null;
			}
			// 合并 50ms 实时窗口与 loading 期缓冲，批量进入 chatStore（insertMessage 会标记脏）
			const buffered = this.realtimeMsgBuffer.splice(0).concat(this.privateMessagesBuffer.splice(0));
			if (buffered.length > 0) {
				this._processBufferedMessages(buffered);
			}
			// 不等 debounce，立即写 IndexedDB（与 initSaveGuard 的 flushSave 幂等共存，重复调用无害）
			this.chatStore.flushSave();
		},
		handleSystemMessage(msg) {
			// 用户被封禁
			if (msg.msgType == this.$enums.MESSAGE_TYPE.USER_BANNED) {
				this.$wsApi.close(3000);
				this.$alert("您的账号已被管理员封禁,原因:" + msg.content, "账号被封禁", {
					confirmButtonText: '确定',
					callback: () => {
						this.onExit();
					}
				});
				return;
			}
		},
		closeUserInfo() {
			this.$refs.userInfo.close();
		},
		onSwtichFullScreen() {
			this.configStore.setFullScreen(!this.configStore.fullScreen);
		},
		onExit() {
			quit().catch(() => {}).finally(() => {
				this.unloadStore();
				this.configStore.setAppInit(false);
				clearTimeout(this.wsConnectTimer);
				if (this.reconnectTimer) {
					clearTimeout(this.reconnectTimer);
					this.reconnectTimer = null;
				}
				this.$wsApi.close(3000);
				sessionStorage.removeItem("accessToken");
				location.href = "/";
			});
		},
		playAudioTip() {
			// 防止过于密集播放
			if (new Date().getTime() - this.lastPlayAudioTime > 1000) {
				this.lastPlayAudioTime = new Date().getTime();
				let audio = new Audio();
				let url = require(`@/assets/audio/tip.mp3`);
				audio.src = url;
				audio.play();
			}
		},
		showSetting() {
			this.showSettingDialog = true;
		},
		closeSetting() {
			this.showSettingDialog = false;
		}
	},
	computed: {
		unreadCount() {
			let unreadCount = 0;
			let chats = this.chatStore.chats;
			chats.forEach(chat => {
				if (!chat.delete && !chat.isDnd) {
					unreadCount += chat.unreadCount
				}
			});
			return unreadCount;
		}
	},
	watch: {
		unreadCount: {
			handler(newCount, oldCount) {
				let tip = newCount > 0 ? `${newCount}条未读` : "";
				this.$elm.setTitleTip(tip);
			},
			immediate: true
		}
	},
	mounted() {
		this.init();
		// F5 整页刷新时 Vue unmounted 不执行，注册 beforeunload/pagehide 兜底 flush 实时缓冲
		window.addEventListener('beforeunload', this._flushBuffersOnUnload);
		window.addEventListener('pagehide', this._flushBuffersOnUnload);
	},
	unmounted() {
		window.removeEventListener('beforeunload', this._flushBuffersOnUnload);
		window.removeEventListener('pagehide', this._flushBuffersOnUnload);
		this.stopPeriodicSync();
		// 性能优化：组件销毁前 flush 实时消息缓冲区，防止消息丢失
		this._flushBuffersOnUnload();
		this.$wsApi.close();
	}
}
</script>

<style scoped lang="scss">
.home-page {
	height: 100vh;
	width: 100vw;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 4px;
	overflow: hidden;
	background: var(--im-color-primary-light-9);

	.app-container {
		width: 62vw;
		height: 80vh;
		display: flex;
		min-height: 600px;
		min-width: 970px;
		position: absolute;
		border-radius: 4px;
		overflow: hidden;
		box-shadow: var(--im-box-shadow-dark);
		transition: 0.2s;

		&.fullscreen {
			transition: 0.2s;
			width: 100vw;
			height: 100vh;
		}
	}

	.navi-bar {
		--icon-font-size: 22px;
		--width: 60px;
		width: var(--width);
		background: var(--im-color-primary-light-1);
		padding-top: 20px;

		.navi-bar-box {
			height: 100%;
			display: flex;
			flex-direction: column;
			justify-content: space-between;

			.botoom {
				margin-bottom: 30px;
			}
		}

		.user-head-image {
			display: flex;
			justify-content: center;
		}

		.menu {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-content: center;
			flex-wrap: wrap;
			margin-top: 20px;

			.link {
				text-decoration: none;
			}

			.router-link-active .menu-item {
				color: white;
				background: var(--im-color-primary-light-2);
			}

			.link:not(.router-link-active) .menu-item:hover {
				background: var(--im-color-primary);
				transform: scale(1.1);
			}

			.menu-item {
				position: relative;
				color: #eee;
				width: var(--width);
				height: 46px;
				width: 46px;
				display: flex;
				justify-content: center;
				align-items: center;
				margin-top: 30px;
				border-radius: 10px;

				.icon {
					font-size: var(--icon-font-size)
				}

				.unread-text {
					position: absolute;
					background-color: var(--im-color-danger);
					left: 28px;
					top: 8px;
					color: white;
					border-radius: 30px;
					padding: 0 5px;
					font-size: var(--im-font-size-smaller);
					text-align: center;
					white-space: nowrap;
					border: 1px solid #f1e5e5;
				}
			}
		}

		.bottom-item {
			display: flex;
			justify-content: center;
			align-items: center;
			height: 50px;
			width: 100%;
			cursor: pointer;
			color: var(--im-color-primary-light-4);
			font-size: var(--icon-font-size);

			.icon {
				font-size: var(--icon-font-size)
			}

			&:hover {
				font-weight: 600;
				color: var(--im-color-primary-light-7);
			}
		}
	}

	.content-box {
		flex: 1;
		padding: 0;
		background-color: #fff;
		text-align: center;
	}
}
</style>